import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { StatusFilter } from './StatusFilter';
import { HybridIntelligentSearchBar } from '@/components/search/HybridIntelligentSearchBar';
import { 
  Grid, List, Map, BarChart3, 
  Users, Activity, Star, TrendingUp
} from 'lucide-react';
import { AdvancedFilterOptions } from '@/hooks/useAdvancedDirectory';
import { UserStatus } from '@/types/presence';
import { statisticsService } from '@/services/statisticsService';
import { useMemberGrowth } from '@/hooks/useMemberGrowth';

interface AdvancedDirectoryHeaderProps {
  totalMembers: number;
  activeMembers: number;
  onlineMembers: number;
  viewMode: 'grid' | 'list' | 'map' | 'analytics';
  onViewModeChange: (mode: 'grid' | 'list' | 'map' | 'analytics') => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  showFilters: boolean;
  onToggleFilters: () => void;
  savedSearches: string[];
  onSaveSearch: () => void;
  filters: AdvancedFilterOptions;
  onFiltersUpdate: (filters: Partial<AdvancedFilterOptions>) => void;
}

export const AdvancedDirectoryHeader: React.FC<AdvancedDirectoryHeaderProps> = ({
  totalMembers,
  activeMembers,
  onlineMembers,
  viewMode,
  onViewModeChange,
  searchTerm,
  onSearchChange,
  showFilters,
  onToggleFilters,
  savedSearches,
  onSaveSearch,
  filters,
  onFiltersUpdate
}) => {
  const [totalConnections, setTotalConnections] = useState(0);
  const { growth, loading: growthLoading } = useMemberGrowth();

  useEffect(() => {
    const loadStats = async () => {
      const stats = await statisticsService.getConnectionStats();
      setTotalConnections(stats.totalConnections);
    };
    loadStats();
  }, []);

  const growthPercentage = growth?.growthPercentage || 0;
  const viewModes = [
    { key: 'grid', icon: Grid, label: 'Grille' },
    { key: 'list', icon: List, label: 'Liste' },
    { key: 'map', icon: Map, label: 'Carte' },
    { key: 'analytics', icon: BarChart3, label: 'Analytics' }
  ];

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Users className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Membres</p>
                <p className="text-2xl font-bold text-foreground">{totalMembers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Activity className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Membres Actifs</p>
                <p className="text-2xl font-bold text-foreground">{activeMembers}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/10 rounded-lg">
                <Star className="h-5 w-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Connexions</p>
                <p className="text-2xl font-bold text-foreground">{totalConnections}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/10 rounded-lg">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Croissance</p>
                <p className="text-2xl font-bold text-foreground">{growthPercentage > 0 ? '+' : ''}{growthPercentage}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search and Controls */}
      <div className="flex flex-col gap-4">
        <HybridIntelligentSearchBar
          onSearchChange={onSearchChange}
          onToggleFilters={onToggleFilters}
          showFilters={showFilters}
          savedSearches={savedSearches}
          placeholder="Recherche intelligente de membres..."
          onSelect={(result) => {
            // Optional: handle member selection from search
            console.log('Member selected:', result);
          }}
        />
      </div>

      {/* View Mode Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">Mode d'affichage:</span>
          <div className="flex bg-muted rounded-lg p-1">
            {viewModes.map(({ key, icon: Icon, label }) => (
              <Button
                key={key}
                variant={viewMode === key ? "default" : "ghost"}
                size="sm"
                onClick={() => onViewModeChange(key as any)}
                className="h-8 px-3"
              >
                <Icon className="h-4 w-4 mr-2" />
                {label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <StatusFilter 
            selectedStatuses={filters.presenceStatus || []}
            onStatusChange={(statuses) => onFiltersUpdate({ presenceStatus: statuses })}
          />
          <Badge variant="outline" className="bg-green-500/10 text-green-700 border-green-200">
            <Activity className="h-3 w-3 mr-1" />
            {onlineMembers} en ligne
          </Badge>
        </div>
      </div>
    </div>
  );
};